import { fromJS, List, Map } from 'immutable';
import * as ids from 'short-id';
import { MediaSegment } from 'redux/modules/entities';
import { DeepImmutableMap } from 'types';
import { MINIMUM_SLIDE_DURATION_MILLIS } from 'utils/constants';
import { addGapsToTime, removeGapsFromTime } from 'utils/embed/media-segment';
import { clamp } from 'utils/numbers';
import { secToMillis } from 'utils/time';
import {
  AdjustLayoutBoundaryAction,
  DeleteLayoutSegmentAction,
  EmbedLayoutSegment,
  EmbedVideoLayoutInfoById,
  IEmbedVideoState,
  SplitLayoutSegmentAction,
  UpdateLayoutSegmentAction,
} from '../types';

function bumpLayoutRevision(state, layoutId: string) {
  return state.updateIn(
    ['videoLayoutInfoById', layoutId, 'revisionId'],
    prevRevisionId => prevRevisionId + 1,
  );
}

function clearLastLayoutActionMeta(state) {
  return state.get('lastLayoutActionMeta')
    ? state.set('lastLayoutActionMeta', undefined)
    : state;
}

export function initializeVideoLayoutInfo(
  state,
  videoLayoutInfoById: EmbedVideoLayoutInfoById,
) {
  return state
    .set('videoLayoutInfoById', videoLayoutInfoById)
    .set('lastLayoutActionMeta', undefined);
}

function adjustLayoutBoundary(
  state,
  data: AdjustLayoutBoundaryAction['payload'],
) {
  const { gapIndex, layoutId, millis, mediaSegments } = data;

  return state.withMutations(s => {
    const valueMillis = addGapsToTime(millis, mediaSegments);

    const layoutSegments = s.getIn([
      'videoLayoutInfoById',
      layoutId,
      'segments',
    ]) as List<DeepImmutableMap<EmbedLayoutSegment>>;

    let isUpdated = false;

    // if there is a segment to the left of the gap, adjust it
    if (gapIndex - 1 >= 0) {
      s.updateIn(
        ['videoLayoutInfoById', layoutId, 'segments', gapIndex - 1],
        prevSegment =>
          prevSegment.set(
            'durationMillis',
            valueMillis - prevSegment.get('playOffsetMillis'),
          ),
      );
      isUpdated = true;
    }

    // if there is a segment to the right of the gap, adjust it
    if (gapIndex < layoutSegments.size) {
      s.updateIn(
        ['videoLayoutInfoById', layoutId, 'segments', gapIndex],
        nextSegment =>
          nextSegment
            .set('playOffsetMillis', valueMillis)
            .set(
              'durationMillis',
              nextSegment.get('durationMillis') -
                (valueMillis - nextSegment.get('playOffsetMillis')),
            ),
      );
      isUpdated = true;
    }

    if (isUpdated) {
      bumpLayoutRevision(s, layoutId);
    }
  });
}

export function handleAdjustLayoutBoundaryAction(
  state,
  action: AdjustLayoutBoundaryAction,
) {
  return clearLastLayoutActionMeta(adjustLayoutBoundary(state, action.payload));
}

export function alignLayoutWithVideo(
  state,
  oldVideo: IEmbedVideoState,
  newVideo: IEmbedVideoState,
  mediaSegments: MediaSegment[],
) {
  const layoutId = newVideo.layoutInfoId;

  if (!layoutId || !state.getIn(['videoLayoutInfoById', layoutId])) {
    return state;
  }

  const oldVideoDurationMillis = oldVideo.endMillis - oldVideo.startMillis;
  const newVideoDurationMillis = newVideo.endMillis - newVideo.startMillis;

  const oldVideoPlayFromMillis = oldVideo.playFromMillis;
  const newVideoPlayFromMillis = newVideo.playFromMillis;

  const oldVideoPlayToMillis = oldVideoPlayFromMillis + oldVideoDurationMillis;
  const newVideoPlayToMillis = newVideoPlayFromMillis + newVideoDurationMillis;

  const isPlayFromMillisChange =
    newVideoPlayFromMillis - oldVideoPlayFromMillis;
  const isPlayToMillisChange = newVideoPlayToMillis - oldVideoPlayToMillis;

  if (isPlayFromMillisChange === 0 && isPlayToMillisChange === 0) {
    return state;
  }

  return state.withMutations(s => {
    if (isPlayFromMillisChange !== 0) {
      const segment = s.getIn(['videoLayoutInfoById', layoutId, 'segments', 0]);

      const segmentPlayToMillis =
        segment.get('playOffsetMillis') + segment.get('durationMillis');

      const maxSegmentPlayFromMillis = addGapsToTime(
        removeGapsFromTime(segmentPlayToMillis, mediaSegments) - 500,
        mediaSegments,
      );

      const newSegmentPlayFromMillis = Math.min(
        addGapsToTime(newVideoPlayFromMillis, mediaSegments),
        maxSegmentPlayFromMillis,
      );

      adjustLayoutBoundary(s, {
        gapIndex: 0,
        layoutId,
        millis: removeGapsFromTime(newSegmentPlayFromMillis, mediaSegments),
        mediaSegments,
      });
    }

    if (isPlayToMillisChange !== 0) {
      const segment = s.getIn([
        'videoLayoutInfoById',
        layoutId,
        'segments',
        -1,
      ]);

      const minSegmentPlayToMillis = addGapsToTime(
        removeGapsFromTime(segment.get('playOffsetMillis'), mediaSegments) +
          500,
        mediaSegments,
      );

      const newSegmentPlayToMillis = Math.max(
        minSegmentPlayToMillis,
        addGapsToTime(newVideoPlayToMillis, mediaSegments),
      );

      adjustLayoutBoundary(s, {
        gapIndex: s.getIn(['videoLayoutInfoById', layoutId, 'segments']).size,
        layoutId,
        millis: removeGapsFromTime(newSegmentPlayToMillis, mediaSegments),
        mediaSegments,
      });
    }

    // currently not tracking any metadata for this action
    clearLastLayoutActionMeta(s);

    return s;
  });
}

function getValidLayoutSplitPositionMillis(
  requestedSplitPositionMillis: number,
  layoutSegment: DeepImmutableMap<EmbedLayoutSegment>,
  mediaSegments: MediaSegment[],
) {
  const positionMillis = removeGapsFromTime(
    requestedSplitPositionMillis,
    mediaSegments,
  );
  const layoutSegmentStartMillis = removeGapsFromTime(
    layoutSegment.get('playOffsetMillis'),
    mediaSegments,
  );
  const layoutSegmentEndMillis = removeGapsFromTime(
    layoutSegment.get('playOffsetMillis') + layoutSegment.get('durationMillis'),
    mediaSegments,
  );

  if (
    layoutSegmentEndMillis - layoutSegmentStartMillis <
    2 * MINIMUM_SLIDE_DURATION_MILLIS
  ) {
    return undefined;
  }

  const adjustedPositionMillis = clamp(
    positionMillis,
    layoutSegmentStartMillis + MINIMUM_SLIDE_DURATION_MILLIS,
    layoutSegmentEndMillis - MINIMUM_SLIDE_DURATION_MILLIS,
  );

  return addGapsToTime(adjustedPositionMillis, mediaSegments);
}

export function splitLayoutSegment(
  state,
  data: SplitLayoutSegmentAction['payload'],
) {
  const { videoId, positionSec, mediaSegments } = data;

  const positionMillis = secToMillis(positionSec);

  const video = state.getIn(['videosById', videoId]);
  const layout = state.getIn(['videoLayoutInfoById', video.layoutInfoId]);

  if (!video || !layout) {
    return clearLastLayoutActionMeta(state);
  }

  const layoutPositionMillis =
    video.playFromMillis + (positionMillis - video.startMillis);

  const rawLayoutPositionMillis = addGapsToTime(
    layoutPositionMillis,
    mediaSegments,
  );

  const segmentIndex = layout
    .get('segments')
    .findIndex(
      seg =>
        rawLayoutPositionMillis >= seg.get('playOffsetMillis') &&
        rawLayoutPositionMillis <=
          seg.get('playOffsetMillis') + seg.get('durationMillis'),
    );

  if (segmentIndex < 0) {
    return clearLastLayoutActionMeta(state);
  }

  const segment = layout.getIn(['segments', segmentIndex]);

  const validLayoutPositionMillis = getValidLayoutSplitPositionMillis(
    rawLayoutPositionMillis,
    segment,
    mediaSegments,
  );

  if (validLayoutPositionMillis === undefined) {
    return state.set(
      'lastLayoutActionMeta',
      Map({
        type: 'split',
        errorMessage: 'Scene is too small to split',
      }),
    );
  }

  const modifiedSegment = segment
    .set('playOffsetMillis', validLayoutPositionMillis)
    .set(
      'durationMillis',
      segment.get('playOffsetMillis') +
        segment.get('durationMillis') -
        validLayoutPositionMillis,
    );

  const newSegment = segment
    .set('segmentId', ids.generate())
    .set(
      'durationMillis',
      validLayoutPositionMillis - segment.get('playOffsetMillis'),
    );

  return bumpLayoutRevision(state, video.layoutInfoId).updateIn(
    ['videoLayoutInfoById', video.layoutInfoId, 'segments'],
    ss =>
      ss
        .insert(segmentIndex, newSegment)
        .set(segmentIndex + 1, modifiedSegment),
  );
}

export function deleteLayoutSegment(
  state,
  data: DeleteLayoutSegmentAction['payload'],
) {
  const { videoId, segmentId } = data;

  const video = state.getIn(['videosById', videoId]);
  const layout = state.getIn(['videoLayoutInfoById', video.layoutInfoId]);

  if (!video || !layout) {
    return clearLastLayoutActionMeta(state);
  }

  const segmentIndex = layout
    .get('segments')
    .findIndex(seg => seg.get('segmentId') === segmentId);

  if (segmentIndex < 0) {
    return clearLastLayoutActionMeta(state);
  }

  return bumpLayoutRevision(state, video.layoutInfoId).updateIn(
    ['videoLayoutInfoById', video.layoutInfoId, 'segments'],
    ss => {
      const deletedSegment = ss.get(segmentIndex);

      if (segmentIndex === 0) {
        return ss
          .update(segmentIndex + 1, nextSegment =>
            nextSegment
              .set('playOffsetMillis', deletedSegment.get('playOffsetMillis'))
              .set(
                'durationMillis',
                nextSegment.get('durationMillis') +
                  deletedSegment.get('durationMillis'),
              ),
          )
          .delete(segmentIndex);
      }

      return ss
        .update(segmentIndex - 1, prevSegment =>
          prevSegment.set(
            'durationMillis',
            prevSegment.get('durationMillis') +
              deletedSegment.get('durationMillis'),
          ),
        )
        .delete(segmentIndex);
    },
  );
}

export function updateLayoutSegmentSlots(
  state,
  data: UpdateLayoutSegmentAction['payload'],
) {
  const { backfill, layoutId, segmentId, slots } = data;

  return state.updateIn(
    ['videoLayoutInfoById', layoutId, 'segments'],
    segments => {
      const index = segments.findIndex(
        segment => segment.get('segmentId') === segmentId,
      );

      if (index < 0) {
        return segments;
      }

      return segments.update(index, seg =>
        seg.withMutations(draftSeg => {
          // NB: per backend, "dynamicCrop" has to be removed from the slot object
          // when we want it to use the static crop exclusively
          const newSlots = fromJS(slots).map(s => s.delete('dynamicCrop'));
          draftSeg.set('slots', newSlots);

          if (backfill) {
            draftSeg.set('backfill', fromJS(backfill));
          }

          return draftSeg;
        }),
      );
    },
  );
}
